import type { FunctionComponent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import type {
  ActionGroupStatus,
  Job,
  RecordActionGroupStatus,
  RecordJobData,
} from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import { ActivityDetailsGroup } from "../ActivityDetailsGroup/ActivityDetailsGroup";
import type { RenderNestedGroups } from "../BranchesGroup/BranchesGroup";
import { findConfigGroupByInstanceId } from "../../../shared/config-walk";
import { useLoopItemOutputs } from "../../hooks/useLazyOutputs";
import { useRootStore } from "../../../../../stores/hooks";
import { deriveLoopName } from "./loop-name";
import { CurrentItemBox } from "./CurrentItemBox";
import { CurrentItemSkeleton } from "./CurrentItemSkeleton";
import {
  currentItemFromLoopOutputs,
  currentItemUsesLoopItemData,
  getCurrentItemRecordJobId,
  getCurrentItemView,
} from "./get-current-item";

interface LoopingBlockProps {
  statusGroup: ActionGroupStatus;
  job: Job;
  renderNestedGroups: RenderNestedGroups;
  recordJobData: Record<string, RecordJobData>;
  fetchRecordJob: (recordJobId: string) => void;
}

interface LoopIterationProps {
  recordAction: RecordActionGroupStatus;
  statusGroup: ActionGroupStatus;
  job: Job;
  renderNestedGroups: RenderNestedGroups;
  recordJobData: Record<string, RecordJobData>;
  fetchRecordJob: (recordJobId: string) => void;
}

// ActivityDetailsGroup keeps its own expand/collapse state internally and
// doesn't expose it, so `renderNestedGroups` (which recurses into this
// iteration's own nested action groups) is gated on a second, local toggle
// kept in lockstep via ActivityDetailsGroup's onClick -- mirrors BranchCard's
// pattern (Task 6/7 lazy-gating finding: JSX children passed to
// ActivityDetailsGroup are otherwise constructed eagerly, i.e. the moment the
// *block* expands, regardless of whether this iteration's own content is
// actually visible).
const LoopIteration: FunctionComponent<LoopIterationProps> = observer(
  ({
    recordAction,
    statusGroup,
    job,
    renderNestedGroups,
    recordJobData,
    fetchRecordJob,
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const handleToggle = useCallback(() => setIsExpanded((prev) => !prev), []);

    // api 7.25.0 stamps a loopItemId per iteration; when present (and the
    // loop is record-list-sourced) the Current Item comes from GetLoopItemData
    // -- the engine resolves the iteration's record server-side. "" marks a
    // pre-7.25 job and falls back to the legacy record-0 workaround below.
    const usesLoopItemData = useMemo(
      () =>
        isExpanded &&
        currentItemUsesLoopItemData(statusGroup, job, recordAction.loopItemId),
      [isExpanded, statusGroup, job, recordAction.loopItemId],
    );
    const loopItemOutputs = useLoopItemOutputs(
      job.jobId,
      recordAction.loopItemId,
      usesLoopItemData,
    );

    // Legacy path (pre-7.25 jobs): a find-action-sourced record list isn't
    // inline in the job status -- the current record is fetched lazily (same
    // recordJobData the rest of run history uses). Trigger lists and output
    // references resolve synchronously and yield no recordJobId.
    const recordJobId = useMemo(
      () =>
        isExpanded && !usesLoopItemData
          ? getCurrentItemRecordJobId(statusGroup, job)
          : undefined,
      [isExpanded, usesLoopItemData, statusGroup, job],
    );

    useEffect(() => {
      if (recordJobId !== undefined) {
        fetchRecordJob(recordJobId);
      }
    }, [recordJobId, fetchRecordJob]);

    // getCurrentItemView walks the config tree and the status tree; memoize
    // so an ancestor re-render doesn't redo both walks per expanded iteration.
    // Depends on loopItemOutputs/recordJobData so the box appears once the
    // lazy fetch resolves.
    const currentItem = useMemo(() => {
      if (!isExpanded) {
        return undefined;
      }
      if (usesLoopItemData) {
        return currentItemFromLoopOutputs(
          loopItemOutputs,
          job.automation?.timezone,
        );
      }
      return getCurrentItemView(
        statusGroup,
        job,
        recordAction.index,
        recordJobData,
      );
    }, [
      isExpanded,
      usesLoopItemData,
      loopItemOutputs,
      statusGroup,
      job,
      recordAction.index,
      recordJobData,
    ]);

    // Only the engine path can distinguish "still loading" (undefined) from
    // "settled with nothing" ([]), so only it shows a skeleton; the legacy
    // record-0 path keeps its old appear-when-ready behavior (its fetch layer
    // can't tell a pending fetch from a failed one -- a skeleton there could
    // get stuck forever).
    const currentItemLoading =
      usesLoopItemData && loopItemOutputs === undefined;

    return (
      <ActivityDetailsGroup
        label={t("job-list.looping-iteration", {
          number: recordAction.index + 1,
        })}
        onClick={handleToggle}
      >
        {isExpanded && (
          <>
            {currentItemLoading ? (
              <CurrentItemSkeleton />
            ) : (
              currentItem !== undefined && <CurrentItemBox item={currentItem} />
            )}
            {/* `true`: this subtree runs inside a loop iteration, so nested
                actions' record jobs live at parent-record-index paths. */}
            {renderNestedGroups(
              recordAction.actionGroups,
              recordAction.index,
              true,
            )}
          </>
        )}
      </ActivityDetailsGroup>
    );
  },
);

export const LoopingBlock: FunctionComponent<LoopingBlockProps> = observer(
  ({ statusGroup, job, renderNestedGroups, recordJobData, fetchRecordJob }) => {
    const { applications } = useRootStore();

    if (statusGroup.status.oneofKind !== "loop") {
      return null;
    }

    const config = findConfigGroupByInstanceId(
      job.automation?.actionGroups ?? [],
      statusGroup.instanceId,
    );
    const loopConfig =
      config?.config.oneofKind === "loop" ? config.config.loop : undefined;
    // `LoopRecordListActionGroupConfig.name` is the snapshot's own field
    // (verified against action_group.d.ts -- not `label`, as candidate-side
    // naming might suggest). It only holds a value when the user set a custom
    // name; the builder leaves it empty when showing the derived default
    // ("Repeat for each in <table>"), so most snapshots have no name
    // (STR-14342). We therefore derive that same default from the loop source
    // here -- resolving the record-list source's table against the app list --
    // and fall back to the generic copy only when even that can't resolve.
    const derivedName =
      loopConfig !== undefined
        ? deriveLoopName(
            loopConfig,
            job.automation?.actionGroups ?? [],
            applications.list,
          )
        : undefined;
    // An empty `name` (the common case -- builder only persists a custom one)
    // must fall through to the derived default, so this checks emptiness
    // explicitly rather than `??` (which would keep the empty string).
    const loopLabel =
      loopConfig?.name !== undefined && loopConfig.name !== ""
        ? loopConfig.name
        : (derivedName ?? t("general.repeat-for-each-in"));

    return (
      <ActivityDetailsGroup
        label={t("job-list.looping-block", { name: loopLabel })}
      >
        {statusGroup.status.loop.recordActions.map((recordAction) => (
          <LoopIteration
            key={`iteration-${recordAction.index}`}
            recordAction={recordAction}
            statusGroup={statusGroup}
            job={job}
            renderNestedGroups={renderNestedGroups}
            recordJobData={recordJobData}
            fetchRecordJob={fetchRecordJob}
          />
        ))}
      </ActivityDetailsGroup>
    );
  },
);
